
import { computed, defineComponent, del, getCurrentInstance, onBeforeUnmount, onMounted, PropType, reactive, set, watch } from 'vue'
import store from '@/store/index'
import { checkRole } from '@root/src/helpers/roles'
import {
    getConnectorAvatarForTable,
    getConnectorPhone,
    getEffectiveConnectors,
} from '@/helpers/channels/channelTableDisplay'
import { openChannelCreationSidebar } from '@/helpers/channels/openChannelCreationSidebar'
import SpinArrows from '@/assets/vuetifyIcons/spinArrows.vue'
import { CHANNEL_TYPES, PROVIDER_TYPES } from '@/vars/general'
import { IntegrationDetailSlot } from '../../../types/marketplace'
import { getAuthStatusRework } from '@/api/networkFixer'

const REWORK_AUTH_PROVIDER_TYPES = [
    PROVIDER_TYPES.whatsappBasis,
    PROVIDER_TYPES.maxBasis,
    PROVIDER_TYPES.whatsappApi,
    PROVIDER_TYPES.telegramApi,
] as string[]

const hasAuthResponseError = (data: unknown, error: unknown) => {
    if (error) return true
    if (data && typeof data === 'object') {
        const payloadError = (data as { error?: unknown }).error
        if (payloadError != null && payloadError !== false) return true
    }
    return false
}

type OverviewRow = {
    key: string
    slotId: string
    slotType?: string | null
    isConnected: boolean
    isDraft?: boolean
    connectorId?: string
    providerType?: string
    alive?: boolean
    name: string
    subtitle?: string
    phone?: string
    groupType: 'tariff' | 'additional'
    groupLabel: string
    avatar?: {
        color: string
        symbol: string
        image: string | null
        name: string
    }
    sidebarItem: Record<string, unknown>
}

const isOverviewConnector = (connector: {
    connectorType?: string
    channelId?: string
    deleted?: boolean
    isDraft?: boolean
}) => {
    if (!connector || connector.deleted) return false
    const channelTypes = Object.values(CHANNEL_TYPES) as string[]
    const providerTypes = Object.values(PROVIDER_TYPES) as string[]
    const isChannel =
        channelTypes.includes(connector.connectorType as string) ||
        providerTypes.includes(connector.connectorType as string)
    return isChannel && (!!connector.channelId || !!connector.isDraft)
}

const buildOverviewRow = (
    slot: IntegrationDetailSlot,
    effectiveConnectors: ReturnType<typeof getEffectiveConnectors>,
    displayOptions: Parameters<typeof getConnectorAvatarForTable>[1]
): OverviewRow => {
    const groupType = slot.isTariff ? 'tariff' : 'additional'
    const groupLabel = slot.isTariff ? 'тариф' : 'доп.'
    const connectorId = slot.channelConnectorId
    const connector = connectorId
        ? effectiveConnectors.find(item => item.connectorId === connectorId)
        : null

    if (connector && isOverviewConnector(connector)) {
        const tableItem = {
            ...connector,
            isActive: true as const,
        }
        const avatar = getConnectorAvatarForTable(tableItem, displayOptions)

        return {
            key: slot.id,
            slotId: slot.id,
            slotType: slot.type,
            isConnected: true,
            isDraft: Boolean(connector.isDraft),
            connectorId: connector.connectorId,
            providerType: connector.providerType,
            alive: Boolean(tableItem.alive),
            name: avatar.name,
            phone: getConnectorPhone(tableItem, effectiveConnectors),
            groupType,
            groupLabel,
            avatar,
            sidebarItem: {
                ...connector,
                _slotId: slot.id,
            },
        }
    }

    return {
        key: slot.id,
        slotId: slot.id,
        slotType: slot.type,
        isConnected: false,
        name: 'Канал не подключён',
        subtitle: 'Подключите или перенесите канал',
        groupType,
        groupLabel,
        sidebarItem: {
            isActive: false,
            _slotId: slot.id,
        },
    }
}

export default defineComponent({
    components: { SpinArrows },
    props: {
        slots: {
            type: Array as PropType<IntegrationDetailSlot[]>,
            default: () => [],
        },
        projectId: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['refresh'],
    setup(props, { emit }) {
        const vm = getCurrentInstance()?.proxy
        const root = getCurrentInstance()?.proxy as { $root?: { $on: Function; $off: Function } } | undefined

        const channelAuthStatusByConnectorId = reactive<Record<string, boolean | null>>({})
        const channelAuthStatusLoadingByConnectorId = reactive<Record<string, boolean>>({})
        const channelAuthStatusReloadQueuedByConnectorId = reactive<Record<string, boolean>>({})

        const project = computed(() => store.getters.projectById(props.projectId))
        const connectorsAvatars = computed(() => store.state.connectorsAvatars)
        const effectiveConnectors = computed(() =>
            getEffectiveConnectors(project.value?.connectors || [])
        )

        const canEdit = computed(() => checkRole(props.projectId, 'project:connector:edit'))

        const displayOptions = computed(() => ({
            effectiveConnectors: effectiveConnectors.value,
            connectorsAvatars: connectorsAvatars.value,
            isChannelsRework: Boolean(project.value?.isChannelsRework),
            whatsappBasisNames: {} as Record<string, string>,
        }))

        const rows = computed<OverviewRow[]>(() =>
            (props.slots || [])
                .map(slot =>
                    buildOverviewRow(slot, effectiveConnectors.value, displayOptions.value)
                )
                .sort((a, b) => {
                    if (a.groupType === b.groupType) return 0
                    return a.groupType === 'tariff' ? -1 : 1
                })
        )

        const shouldUseReworkAuthStatus = (row: OverviewRow) => {
            if (!row.isConnected) return false
            return REWORK_AUTH_PROVIDER_TYPES.includes(String(row.providerType || ''))
        }

        const getAuthStatusConnectorId = (row: OverviewRow) => row.connectorId || null

        const isAuthStatusFailed = (row: OverviewRow) => {
            if (!shouldUseReworkAuthStatus(row)) return false
            const connectorId = getAuthStatusConnectorId(row)
            if (!connectorId) return false
            return channelAuthStatusByConnectorId[connectorId] === null
        }

        const isAuthStatusLoading = (row: OverviewRow) => {
            if (!shouldUseReworkAuthStatus(row)) return false
            const connectorId = getAuthStatusConnectorId(row)
            if (!connectorId) return false
            return Boolean(channelAuthStatusLoadingByConnectorId[connectorId])
        }

        const isChannelAuthorized = (row: OverviewRow) => {
            if (!row.isConnected) return false
            if (shouldUseReworkAuthStatus(row)) {
                const connectorId = getAuthStatusConnectorId(row)
                if (!connectorId) return false
                if (channelAuthStatusLoadingByConnectorId[connectorId]) return false
                const reworkStatus = channelAuthStatusByConnectorId[connectorId]
                if (reworkStatus !== undefined && reworkStatus !== null) {
                    return reworkStatus === true
                }
                return row.alive === true
            }
            return row.alive === true
        }

        const getAuthStatusLabel = (row: OverviewRow) => {
            if (!row.isConnected) return ''
            if (isAuthStatusLoading(row)) return 'Проверка...'
            if (isAuthStatusFailed(row)) return 'Не удалось проверить'
            if (isChannelAuthorized(row)) return 'Подключен'
            return 'Не подключен'
        }

        const requestReworkAuthStatus = async (connectorId: string) => {
            set(channelAuthStatusLoadingByConnectorId, connectorId, true)
            try {
                const { data, error } = await getAuthStatusRework({ connectorId })
                if (hasAuthResponseError(data, error)) {
                    set(channelAuthStatusByConnectorId, connectorId, null)
                    return
                }
                set(channelAuthStatusByConnectorId, connectorId, data === 'authorized')
            } catch (e) {
                set(channelAuthStatusByConnectorId, connectorId, null)
                console.error('Ошибка загрузки authStatusRework:', e)
            } finally {
                set(channelAuthStatusLoadingByConnectorId, connectorId, false)
                if (channelAuthStatusReloadQueuedByConnectorId[connectorId]) {
                    del(channelAuthStatusReloadQueuedByConnectorId, connectorId)
                    await requestReworkAuthStatus(connectorId)
                }
            }
        }

        const fetchReworkAuthStatus = async (row: OverviewRow) => {
            const connectorId = getAuthStatusConnectorId(row)
            if (!connectorId || !shouldUseReworkAuthStatus(row)) return
            if (channelAuthStatusLoadingByConnectorId[connectorId]) return
            if (channelAuthStatusByConnectorId[connectorId] !== undefined) return
            await requestReworkAuthStatus(connectorId)
        }

        const reloadReworkAuthStatus = async (connectorId: string) => {
            if (!connectorId) return
            if (channelAuthStatusLoadingByConnectorId[connectorId]) {
                set(channelAuthStatusReloadQueuedByConnectorId, connectorId, true)
                return
            }
            del(channelAuthStatusByConnectorId, connectorId)
            await requestReworkAuthStatus(connectorId)
        }

        const prefetchReworkAuthStatuses = () => {
            rows.value.forEach(row => {
                if (!shouldUseReworkAuthStatus(row)) return
                void fetchReworkAuthStatus(row)
            })
        }

        const reloadAllReworkAuthStatuses = () => {
            rows.value.forEach(row => {
                if (!shouldUseReworkAuthStatus(row) || !row.connectorId) return
                void reloadReworkAuthStatus(row.connectorId)
            })
        }

        const handleReloadAuthStatus = (payload: string | { connectorId?: string }) => {
            const connectorId = typeof payload === 'string' ? payload : payload?.connectorId
            if (!connectorId) return
            const row = rows.value.find(item => item.connectorId === connectorId)
            if (row && shouldUseReworkAuthStatus(row)) {
                void reloadReworkAuthStatus(connectorId)
            }
        }

        const handleRefresh = () => {
            emit('refresh')
            reloadAllReworkAuthStatuses()
        }

        const reworkAuthConnectorIdsKey = computed(() =>
            rows.value
                .filter(shouldUseReworkAuthStatus)
                .map(row => row.connectorId)
                .filter((id): id is string => Boolean(id))
                .sort()
                .join(',')
        )

        watch(reworkAuthConnectorIdsKey, () => {
            prefetchReworkAuthStatuses()
        }, { immediate: true })

        onMounted(() => {
            root?.$root?.$on('reload-channel-auth-status', handleReloadAuthStatus)
        })

        onBeforeUnmount(() => {
            root?.$root?.$off('reload-channel-auth-status', handleReloadAuthStatus)
        })

        const openDetails = (row: OverviewRow) => {
            if (!row.connectorId) return
            store.commit('setConnectorSettingsSidebarId', row.connectorId)
            store.commit('setIsConnectorSettingsSidebar', true)
            store.commit('setChannelCreationMode', false)
            store.commit('setChannelCreationStage', null)
        }

        const openConnect = (row: OverviewRow) => {
            if (!vm) return
            const opened = openChannelCreationSidebar(vm, row.sidebarItem, 'new')
            if (!opened) return
            if (row.slotType === 'maxSlot') {
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('group', 'maxBusiness')
                window.history.replaceState({}, '', newUrl.toString())
            }
        }

        return {
            rows,
            canEdit,
            openDetails,
            openConnect,
            handleRefresh,
            isAuthStatusLoading,
            isAuthStatusFailed,
            isChannelAuthorized,
            getAuthStatusLabel,
            channelAuthStatusLoadingByConnectorId,
            channelAuthStatusByConnectorId,
        }
    },
})
